import React, { useMemo } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { Calendar } from '@fabric-ui/icons';
import { FailFull as Error } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { emptyArray } from '@fabric-ui/utils';
import { alpha, Box, SxProps, Theme } from '@mui/material';
import { format, isSameYear, isValid, parse } from 'date-fns';
import Select from './Select/Select';

const calendarIcon = <Calendar />;
const errorIcon = <Error />;

const MINUTE = 1000 * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const MONTH = DAY * 30;
const YEAR = MONTH * 12;

function timeSince(timeSpan) {
  // eslint-disable-next-line custom-rules/no-let
  let interval = timeSpan / YEAR;
  if (interval >= 1) {
    return `${Math.floor(interval)}y`;
  }
  interval = timeSpan / MONTH;
  if (interval >= 1) {
    return `${Math.floor(interval)}mo`;
  }
  interval = timeSpan / DAY;
  if (interval >= 1) {
    return `${Math.floor(interval)}d`;
  }
  interval = timeSpan / HOUR;
  if (interval >= 1) {
    return `${Math.floor(interval)}h`;
  }
  interval = timeSpan / MINUTE;
  if (interval >= 1) {
    return `${Math.floor(interval)}m`;
  }
  return `${Math.floor(timeSpan / 1000)}s`;
}

export const dateFilterOptions = [
  {
    title: 'Past 15 Minutes',
    value: MINUTE * 15,
    shortVal: '15m',
  },
  {
    title: 'Past 1 Hour',
    value: HOUR,
    shortVal: '1h',
  },
  {
    title: 'Past 4 Hours',
    value: HOUR * 4,
    shortVal: '4h',
  },
  {
    title: 'Past 1 Day',
    value: DAY,
    shortVal: '1d',
  },
  {
    title: 'Past 2 Days',
    value: DAY * 2,
    shortVal: '2d',
  },
  {
    title: 'Past 3 Days',
    value: DAY * 3,
    shortVal: '3d',
  },
  {
    title: 'Past 7 Days',
    value: DAY * 7,
    shortVal: '7d',
  },
  {
    title: 'Past 15 Days',
    value: DAY * 15,
    shortVal: '15d',
  },
  {
    title: 'Past 30 Days',
    value: DAY * 30,
    shortVal: '1mo',
  },
  {
    title: 'Past 90 Days',
    value: DAY * 90,
    shortVal: '3mo',
  },
];
export const allTimesOption = { title: 'All Time', value: DAY * 365 * 15, shortVal: 'ALL' };

const getOptions = showAllOption => (showAllOption ? [allTimesOption, ...dateFilterOptions] : dateFilterOptions);

export const dateOptionStyle = theme => ({
  ...flex.row,
  gap: '16px',
  '.interval-shortval': {
    width: 56,
    borderRadius: '8px',
    backgroundColor: alpha(theme.palette.colors.neutrals[350], 0.88),
    color: theme.palette.colors.neutrals[600],
    fontWeight: 600,
    ...flex.justifyCenter,
    '[aria-selected="true"] &': {
      backgroundColor: alpha(theme.palette.colors.primary[200], 0.88),
    },
  },
});

const inputPrefixStyle = {
  ...flex.itemsCenter,
  gap: '10px',
  '> div > .interval-shortval': {
    backgroundColor: theme => theme.palette.colors.primary[150],
    '+ span': {
      display: 'none',
    },
  },
};
const formatDate = (date, token) => (isValid(date) ? format(date, `${token}, p`) : 'Invalid');
const getValueFunc = val =>
  Number.isNaN(val.value)
    ? val.value
    : val.value
      ? [new Date(Date.now() - val.value), new Date()]
      : val === allTimesOption.title || val.value === 0
        ? []
        : val.split('-').map(date => {
            const parsedDate = parse(date.trim(), 'MMM dd, yyyy, p', new Date());
            return isValid(parsedDate) ? parsedDate : parse(date.trim(), 'MMM dd, p', new Date());
          });

interface SelectDateProps {
  value: Date[];
  onChange: (value: Date[]) => void;
  error?: string;
  showAllTimeOption?: boolean;
  sx?: SxProps<Theme>;
  defaultDisplayValue?: { title: string; shortVal: string };
}

// TODO: MAKE IT READABLE + Add Specs
export function SelectDate({
  value = emptyArray,
  onChange,
  error: e,
  showAllTimeOption = false,
  sx,
  defaultDisplayValue = allTimesOption,
}: SelectDateProps): React.JSX.Element {
  const now = useMemo(() => new Date(), []);

  const renderOption = ({ shortVal, title }: { shortVal: string; title: string }) => (
    <Box sx={dateOptionStyle}>
      <span className="interval-shortval">{shortVal}</span>
      <span>{title}</span>
    </Box>
  );

  const startAdornment = (val: { shortVal: string; title: string }) => (
    <Box sx={inputPrefixStyle}>
      {calendarIcon}
      {renderOption(val)}
    </Box>
  );

  const formatToken = value.length && isSameYear(now, value[0]) && isSameYear(value[0], value[1]) ? 'MMM dd' : 'MMM dd, yyyy';
  const timeSpan = value.length ? value[1].getTime() - value[0].getTime() : 0;
  const error = e || (value.length && Number.isNaN(timeSpan) ? 'Invalid Date' : undefined);

  const select = (
    <Select
      // @ts-ignore
      value={{
        dateLabel: value.length ? `${formatDate(value[0], formatToken)} - ${formatDate(value[1], formatToken)}` : defaultDisplayValue.title,
        shortVal: error ? 'X' : value.length ? timeSince(timeSpan) : defaultDisplayValue.shortVal,
        value: timeSpan,
      }}
      valueAsObject
      onChange={(val: any, opt: any) => onChange(opt.title === allTimesOption.title ? [] : val)}
      // @ts-ignore
      options={getOptions(showAllTimeOption).map(opt => {
        const optDate = new Date(Date.now() - opt.value);
        const formatToken = isSameYear(optDate, now) ? 'MMM dd' : 'MMM dd, yyyy';
        return {
          ...opt,
          dateLabel:
            opt.title === allTimesOption.title
              ? allTimesOption.title
              : `${formatDate(optDate, formatToken)} - ${formatDate(now, formatToken)}`,
        };
      })}
      // @ts-ignore
      renderOption={renderOption}
      getValueFunc={getValueFunc}
      // @ts-ignore
      getLabelFunc={(val: { dateLabel: string }) => val.dateLabel || val}
      isRequired
      size="small"
      // @ts-ignore
      startAdornment={startAdornment}
      freeSolo
      error={error}
      // @ts-ignore
      style={{
        '&, & > .MuiTextField-root': { maxWidth: '400px' },
        '.MuiOutlinedInput-input::selection': { backgroundColor: (theme: Theme) => theme.palette.colors.primary[150] },
      }}
      sx={sx}
      // @ts-ignore
      muiProps={{ autoSelect: true }}
    />
  );

  return error ? (
    <AvTooltip
      variant="message"
      color="white"
      title={
        <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
          {errorIcon}
          {error}
        </Box>
      }>
      <Box sx={{ width: 400 }}>{select}</Box>
    </AvTooltip>
  ) : (
    select
  );
}
